import * as React from "react";

/**
 * PWA install support.
 *
 * Chrome-family browsers fire `beforeinstallprompt` when the page is
 * installable and the user is engaged; iOS Safari never fires it — Apple
 * exposes no programmatic install API, so iOS callers show guided
 * instructions instead. `BeforeInstallPromptEvent` is not in TS 4.9's DOM
 * lib, hence the local interface.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isStandaloneMode(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function detectIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ reports a Macintosh UA but keeps a touchscreen.
  return (
    /iP(ad|hone|od)/.test(ua) ||
    (ua.includes("Macintosh") && navigator.maxTouchPoints > 1)
  );
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = React.useState<BeforeInstallPromptEvent | null>(null);
  const [standalone, setStandalone] = React.useState<boolean>(isStandaloneMode);

  React.useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      // Suppress the browser's own mini-infobar so the in-app banner is the
      // single install surface.
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setStandalone(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = React.useCallback(async () => {
    const prompt = deferredPrompt;
    if (!prompt) return;
    setDeferredPrompt(null);
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice.outcome === "accepted") setStandalone(true);
    } catch {
      // Prompt can reject when the browser no longer allows showing it;
      // keep the banner available for the next engagement.
      setDeferredPrompt(prompt);
    }
  }, [deferredPrompt]);

  const isIos = React.useMemo(detectIos, []);

  return {
    isIos,
    standalone,
    /** True when the banner should render: not installed, and either iOS (manual install) or an installable Chromium session. */
    showInstall: !standalone && (isIos || deferredPrompt !== null),
    promptInstall,
  };
}
